import classNames from "classnames";
import DOMPurify from "dompurify";
import { fromJS } from "immutable";
import React from "react";
import { injectIntl, IntlShape } from "react-intl";
import styled from "styled-components";

import FieldsHOC, { FieldHocProps } from "../fields-hoc";
import UpInput from "../up-input";
import messages from "./messages";

export type SelectBoxValue = string | number;

export interface SelectBoxOption {
  label: string | number;
  value: SelectBoxValue;
  disabled?: boolean;
  inactive?: boolean;
}

export interface SelectBoxOwnProps {
  options: readonly SelectBoxOption[];
  value?: SelectBoxValue;
  onChange(name: string, value: SelectBoxValue): void;
  name: string;
  sort?: boolean;
  placeholder?: React.InputHTMLAttributes<HTMLInputElement>["placeholder"];
  inputDisabled?: boolean;
  disabled?: boolean;
  className?: string;
  margin?: string;
  padding?: string;
  minWidth?: string;
  width?: string;
  optionsPosition?: "top" | "bottom";
  emptyMessage?: string;
}

interface SelectBoxProps extends SelectBoxOwnProps {
  intl: IntlShape;
}

interface SelectBoxState {
  mounted: boolean;
  optionsVisible: boolean;
  visibleOptions: SelectBoxOption[];
  input: string;
  width: number;
  selectedOption: string;
  focusedOption: number;
  controls: "" | "keys" | "mouse";
  focused: boolean;
  mouseOverOptions: boolean;
}

type OptionsStyleProps = Pick<SelectBoxOwnProps, "optionsPosition"> & {
  width: number;
};

type WrapperStyleProps = Pick<
  SelectBoxOwnProps,
  "margin" | "padding" | "minWidth" | "width"
>;

const Input = styled(UpInput)`
  margin-right: 5px;
  border: none;
  padding: 0px;
  background: none;
  overflow-x: auto;
  height: 100%;
  &:focus {
    outline: none;
    border: none;
    box-shadow: none;
  }
  &:disabled {
    -webkit-user-select: none; /* Safari 3.1+ */
    -moz-user-select: none; /* Firefox 2+ */
    -ms-user-select: none; /* IE 10+ */
    user-select: none; /* Standard syntax */
    background: none;
    cursor: pointer;
  }
`;

const Options = styled.div<OptionsStyleProps>`
  position: absolute;
  background: #14171c;
  width: ${(props) => props.width}px;
  border-radius: 3px;
  z-index: 10;
  max-height: 250px;
  overflow-y: scroll;
  bottom: ${(props) => (props.optionsPosition === "top" ? "56px" : "auto")};
`;

const Option = styled.div`
  padding: 10px;
  cursor: pointer;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  &.inactiveOption {
    color: #888888;
    pointer-events: none;
  }
  &.activeOption {
    background: #3dd3ce;
  }
  &.focusedOption {
    background: #1f242e;
  }
`;

const SelectBoxWrapper = styled.div`
  border-radius: 3px;
  background: #14171c;
  &.disabled {
    background: #efefef26;
  }
`;

const Wrapper = styled.div<WrapperStyleProps>`
  margin: ${(props) => props.margin ?? "0px"};
  padding: ${(props) => props.padding ?? "0px"};
  min-width: ${(props) => (props.minWidth ? props.minWidth : "auto")};
  width: ${(props) => props.width || "auto"};
`;

const InputWrapper = styled.div`
  padding: 7px 15px 7px 10px;
  border-radius: 3px;
  width: 100%;
  border: ${(props) => props.theme.inputBorder};
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.fontColor};
  position: relative;
  height: 36px;
  svg {
    height: 15px;
    width: 15px;
    fill: ${(props) => props.theme.fontColor};
  }
  &.disabled {
    background-color: #efefef26;
    &.focused {
      box-shadow: 0px 0px 5px #efefef26;
    }
  }
  &.focused:not(.disabled) {
    ${(props) => props.theme.inputFocus}
  }
  &.error {
    padding-right: 22px;
    border-color: ${(props) => props.theme.primaryActionColor};
    background-color: #f5d1d2;
  }
`;

class SelectBox extends React.PureComponent<SelectBoxProps, SelectBoxState> {
  node: HTMLDivElement | null = null;

  input: HTMLInputElement | null = null;

  textInput: HTMLDivElement | null = null;

  focusedOption: HTMLDivElement | null = null;

  constructor(props: SelectBoxProps) {
    super(props);

    this.state = {
      mounted: false,
      optionsVisible: false,
      visibleOptions: props.sort
        ? props.options
            .filter((option) => !option.disabled)
            .sort((a, b) => this.alphabeticOrder(a, b))
        : props.options.filter((option) => !option.disabled),
      input: this.getLabel(false, props.value),
      width: 0,
      selectedOption: "",
      focusedOption: 0,
      controls: "",
      focused: false,
      mouseOverOptions: true
    };
  }

  componentDidMount(): void {
    this.setState((currentState) => ({
      mounted: true,
      input: this.getLabel(
        currentState.mounted,
        this.props.value,
        this.props.options
      )
    }));
  }

  componentDidUpdate(
    prevProps: Readonly<SelectBoxProps>,
    prevState: Readonly<SelectBoxState>
  ): void {
    const optionsChanged = !fromJS(prevProps.options).equals(
      fromJS(this.props.options)
    );
    if (prevProps.value !== this.props.value || optionsChanged) {
      this.updateInput();
    }

    if (optionsChanged) {
      const newVisibleOptions = this.props.sort
        ? this.props.options
            .filter((option) => !option.disabled)
            .sort((a, b) => this.alphabeticOrder(a, b))
        : this.props.options.filter((option) => !option.disabled);
      this.setState((currentState) => ({
        visibleOptions: newVisibleOptions,
        focusedOption: Math.min(
          currentState.focusedOption,
          Math.max(0, newVisibleOptions.length - 1)
        )
      }));
    }

    if (
      prevState.focusedOption !== this.state.focusedOption &&
      this.state.controls === "keys"
    ) {
      /** when the user uses the arrow keys to scroll through the options it will automatically scroll */
      if (this.focusedOption && this.textInput) {
        this.textInput.scrollTop = this.focusedOption.offsetTop - 40;
      }
    }
    if (
      this.state.optionsVisible === true &&
      prevState.optionsVisible !== this.state.optionsVisible
    ) {
      /** on opening the options it scrolls automatically to the selected value */
      if (this.focusedOption && this.textInput) {
        this.textInput.scrollTop = this.focusedOption.offsetTop - 40;
      }
    }
  }

  updateInput = (): void => {
    this.setState((currentState) => ({
      input: this.getLabel(
        currentState.mounted,
        this.props.value,
        this.props.options
      )
    }));
  };

  /**
   * close options component when the user clicks anywhere
   */
  closeOptions = (event: MouseEvent): void => {
    if (this.node) {
      if (event.target instanceof Node && this.node.contains(event.target)) {
        return;
      }
      this.setState({
        optionsVisible: false,
        input: this.getLabel(Boolean(this.props.value)),
        focused: false
      });
      document.removeEventListener("click", this.closeOptions, false);
      window.removeEventListener("resize", this.updateDimensions, false);
    }
  };

  /**
   * when the browser window is resized the options menu must be resized too
   */
  updateDimensions = (): void => {
    if (this.node) {
      this.setState({
        width: this.node.offsetWidth
      });
    }
  };

  /**
   * function to open the options menu
   */

  openOptions(): void {
    document.addEventListener("click", this.closeOptions, false);
    window.addEventListener("resize", this.updateDimensions, false);
    if (!this.props.disabled && this.input && this.node) {
      this.input.focus();
      const selectedOption = this.props.options.find(
        (option) => option.value === this.props.value
      );
      this.setState({
        optionsVisible: true,
        width: this.node.offsetWidth,
        focusedOption:
          this.props.value === ""
            ? 0
            : selectedOption
              ? this.props.options.indexOf(selectedOption)
              : -1,
        visibleOptions: this.props.sort
          ? this.props.options
              .filter((option) => !option.disabled)
              .sort((a, b) => this.alphabeticOrder(a, b))
          : this.props.options.filter((option) => !option.disabled),
        focused: true
      });
    }
  }

  // function to select an option

  selectOption(option: SelectBoxOption): void {
    this.setState({
      optionsVisible: false,
      input: String(option.label),
      focused: false
    });
    this.props.onChange(this.props.name, option.value);
  }

  /**
   * this functions handles the arrow keys control and the close of the options on tab
   * @param event - event interface is a reference to the object onto which the event was dispatched.
   */

  handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.keyCode === 38) {
      /** on arrow key up */
      event.preventDefault();
      if (!this.state.optionsVisible) {
        this.openOptions();
      } else {
        this.setState((currentState) => ({
          focusedOption:
            (((currentState.focusedOption - 1) %
              currentState.visibleOptions.length) +
              currentState.visibleOptions.length) %
            currentState.visibleOptions.length,
          controls: "keys"
        }));
      }
    } else if (event.keyCode === 40) {
      /** on arrow key down */
      event.preventDefault();
      if (!this.state.optionsVisible) {
        this.openOptions();
      } else {
        this.setState((currentState) => ({
          focusedOption:
            (currentState.focusedOption + 1) %
            currentState.visibleOptions.length,
          controls: "keys"
        }));
      }
    } else if (event.keyCode === 9 || event.keyCode === 27) {
      /** on tab or esc close the options */
      this.setState({
        optionsVisible: false,
        input: this.getLabel(Boolean(this.props.value)),
        focused: false
      });
    } else if (event.keyCode === 13) {
      /** on enter select the focused option */
      event.preventDefault();
      if (this.state.visibleOptions.length !== 0) {
        const focusedOption =
          this.state.visibleOptions[this.state.focusedOption];
        this.setState({
          input: String(focusedOption.label)
        });
        this.selectOption(focusedOption);
      }
    }
  }

  /**
   * handles the hover function for the options
   * @param index - index from the map function
   */

  handleMouseOver(index: number): void {
    this.setState({
      focusedOption: index,
      controls: "mouse"
    });
  }

  /**
   * sort function for the options
   */

  alphabeticOrder(a: SelectBoxOption, b: SelectBoxOption): number {
    const firstLabel = String(a.label).toLowerCase();
    const secondLabel = String(b.label).toLowerCase();
    if (firstLabel === secondLabel) return 0;
    return firstLabel < secondLabel ? -1 : 1;
  }

  /**
   * gets called when the user types something in the input field and then filters the options based on the input value
   * @param event - event interface is a reference to the object onto which the event was dispatched.
   */

  inputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const input = event.target.value.toLowerCase();
    this.setState({
      input: event.target.value,
      optionsVisible: true,
      focusedOption: 0,
      focused: true,
      visibleOptions: this.props.sort
        ? this.props.options
            .filter(
              (option) =>
                !option.disabled &&
                String(option.label).toLowerCase().includes(input)
            )
            .sort((a, b) => this.alphabeticOrder(a, b))
        : this.props.options.filter(
            (option) =>
              !option.disabled &&
              String(option.label).toLowerCase().includes(input)
          )
    });
  }

  handleMouseLeave(): void {
    this.setState({
      mouseOverOptions: false,
      focusedOption: -1
    });
  }

  /**
   * Get default option for selectBox
   * @param mounted - check if component has been mounted for the first render
   * set to 'true' after componentDidMount
   * @param value - value for the option coming from props
   * @param options - list of select options coming from props
   */

  getLabel(
    mounted: boolean,
    value: SelectBoxValue | undefined = this.props.value,
    options: readonly SelectBoxOption[] = this.props.options
  ): string {
    const option = options.find((candidate) => candidate.value === value);

    if (
      !option &&
      (value === "" || value === undefined) &&
      mounted &&
      this.props.placeholder
    ) {
      return "";
    }

    if (!option && (value === "" || value === undefined) && mounted) {
      // eslint-disable-next-line no-console
      console.warn(
        `no default value found value: ${value} type ${typeof value} options: ${JSON.stringify(
          options
        )}`
      );
      return "--";
    }

    if (!option && mounted) {
      // eslint-disable-next-line no-console
      console.error(
        `NO OPTION FOUND with value: ${value} type ${typeof value} options: ${JSON.stringify(
          options
        )}`
      );
      return "-!-";
    }

    if (option && typeof option.label === "string" && mounted) {
      return option.label.replace(/(<([^>]+)>)/gi, "");
    }
    return option ? String(option.label) : "";
  }

  handleMouseEnter(): void {
    this.setState({
      mouseOverOptions: true
    });
  }

  render(): React.ReactNode {
    return (
      <Wrapper
        className={this.props.className}
        margin={this.props.margin}
        padding={this.props.padding}
        minWidth={this.props.minWidth}
        width={this.props.width}
      >
        <SelectBoxWrapper
          className={this.props.disabled ? "disabled" : undefined}
        >
          <div
            ref={(node) => {
              this.node = node;
            }}
          >
            <InputWrapper
              tabIndex={this.props.disabled ? -1 : 0}
              onClick={(event) => {
                this.openOptions();
                if (event.target instanceof HTMLInputElement) {
                  event.target.select();
                }
              }}
              onFocus={() => this.openOptions()}
              onKeyDown={(event) => this.handleKeyDown(event)}
              className={classNames(this.props.className, {
                focused: this.state.focused,
                disabled:
                  Boolean(this.state.input) &&
                  this.props.options.some(
                    (option) =>
                      option.value === this.props.value && option.disabled
                  )
              })}
            >
              <Input
                tabIndex={-1}
                placeholder={this.props.placeholder}
                value={this.state.input}
                onChange={(event) => this.inputChange(event)}
                ref={(input) => {
                  this.input = input;
                }}
                disabled={this.props.inputDisabled}
                width="100%"
              />
              <svg viewBox="0 0 2.13 3.64">
                <g id="Layer_2" data-name="Layer 2">
                  <g id="Layer_1-2" data-name="Layer 1">
                    <polygon
                      className="cls-1"
                      points="0 1.49 1.07 0 2.13 1.49 0 1.49"
                    />
                    <polygon
                      className="cls-1"
                      points="2.13 2.15 1.07 3.64 0 2.15 2.13 2.15"
                    />
                  </g>
                </g>
              </svg>
            </InputWrapper>
            {this.state.optionsVisible && (
              <Options
                className="selectBox--options"
                onMouseLeave={() => this.handleMouseLeave()}
                onMouseEnter={() => this.handleMouseEnter()}
                width={this.state.width}
                ref={(textInput) => {
                  this.textInput = textInput;
                }}
                optionsPosition={this.props.optionsPosition}
              >
                {this.state.visibleOptions.length === 0 &&
                  this.props.emptyMessage && (
                    <Option>{this.props.emptyMessage}</Option>
                  )}
                {this.state.visibleOptions.map((option, index) => (
                  <Option
                    className={classNames({
                      activeOption: this.props.value === option.value,
                      inactiveOption: option.inactive,
                      focusedOption:
                        this.state.focusedOption === index &&
                        this.props.value !== option.value &&
                        (this.state.mouseOverOptions ||
                          this.state.controls === "keys")
                    })}
                    key={`${typeof option.value}:${option.value}`}
                    onClick={() => this.selectOption(option)}
                    onMouseMove={() => this.handleMouseOver(index)}
                    ref={
                      this.state.focusedOption === index
                        ? (focusedOption) => {
                            this.focusedOption = focusedOption;
                          }
                        : null
                    }
                    title={this.props.intl.formatMessage(messages.select, {
                      option: option.label
                    })}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(String(option.label))
                    }}
                  />
                ))}
              </Options>
            )}
          </div>
        </SelectBoxWrapper>
      </Wrapper>
    );
  }
}

const InjectedSelectBox = injectIntl(SelectBox);

export default InjectedSelectBox;

const StyledSelectBox = styled(SelectBox)`
  margin: ${(props) => props.margin || "0 5px 0 0"};
  @media only screen and (max-width: 470px) {
    margin: ${(props) => props.margin || "0 10px 0 0"};
  }
`;

interface SelectBoxFieldInput {
  name: string;
  value?: SelectBoxValue;
  onChange(value: SelectBoxValue): void;
  onBlur?: unknown;
  onFocus?: unknown;
}

interface SelectBoxFieldCustomProps {
  options: readonly SelectBoxOption[];
  onChange?(name: string, value: SelectBoxValue): void;
  minWidth?: string;
  inputDisabled?: boolean;
}

type UnwrappedSelectBoxProps = Omit<FieldHocProps, "input" | "meta"> & {
  input: SelectBoxFieldInput;
  meta: FieldHocProps["meta"] & {
    touched?: boolean;
  };
  customProps: SelectBoxFieldCustomProps;
  placeholder?: React.InputHTMLAttributes<HTMLInputElement>["placeholder"];
  defaultValue?: SelectBoxValue;
  margin?: string;
  padding?: string;
  intl: IntlShape;
};

const unwrappedSelectBox = ({
  input,
  meta,
  customProps,
  placeholder,
  defaultValue = "",
  margin,
  padding,
  intl
}: UnwrappedSelectBoxProps) => (
  <StyledSelectBox
    options={customProps.options}
    value={input.value || defaultValue}
    name={input.name}
    onChange={(name, value) => {
      input.onChange(value);
      customProps.onChange?.(name, value);
    }}
    minWidth={customProps.minWidth}
    inputDisabled={customProps.inputDisabled === undefined}
    placeholder={placeholder}
    className={meta.error && meta.submitFailed ? "error" : undefined}
    margin={margin}
    padding={padding}
    intl={intl}
  />
);

const selectBox = injectIntl(FieldsHOC(unwrappedSelectBox));

export { selectBox };
